// Powerlete mobile menu — gender tab switcher (WOMENS / MENS).
// The drawer open/close/scroll-lock/focus-trap/Escape/backdrop is Horizon's
// native <header-drawer> (header-drawer.js); this only swaps the two tab panels.
// Accessible tablist: roving tabindex, arrow/Home/End keys, aria-selected.
class PowerleteGenderTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (this.tabs.length < 2) {
      // A single-tab drawer (e.g. Mens-only) can render its lone tab aria-selected=false with a
      // hidden panel (the static markup only pre-selects tab 1). With no sibling to switch to,
      // force-select it so the drawer body isn't left empty.
      if (this.tabs.length === 1) this.select(0);
      return;
    }

    this.activeIndex = 0;

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, i));
    });

    // Replay the active panel's entrance animation on every drawer open so the
    // content always feels fresh (consistent with Gymshark / Oner / AYBL). The
    // Horizon header drawer opens via a `.menu-open` CLASS on the <details>
    // (header-drawer.js), NOT the native [open] attribute, so the `toggle` event
    // never fires — watch the class instead. CSS handles reduced-motion (setting
    // style.animation = '' defers to the stylesheet's prefers-reduced-motion rule).
    const details = this.closest('details');
    if (details) {
      let wasOpen = details.classList.contains('menu-open');
      this.menuObserver = new MutationObserver(() => {
        const isOpen = details.classList.contains('menu-open');
        if (isOpen && !wasOpen) {
          this.syncHeaderOffset(details);
          this.replayActivePanel();
        }
        else if (!isOpen && wasOpen) this.playCloseCascade();
        wasOpen = isOpen;
      });
      this.menuObserver.observe(details, { attributes: true, attributeFilter: ['class'] });
    }
  }

  // The panel's resting top offset (--header-group-height, set by header.js) is the
  // announcement-bar + header height measured once via ResizeObserver. It only tracks
  // LAYOUT size changes, so once the (non-sticky) announcement bar scrolls out of view
  // and only the sticky header row remains pinned, that variable is stale — the panel
  // opens using the old, taller offset and leaves a gap of real page content visible
  // between the header and the panel. Measure the sticky header's actual on-screen
  // bottom edge right as the panel opens and override the offset with that.
  syncHeaderOffset(details) {
    const header = document.querySelector('#header-component');
    if (!header) return;
    const bottom = Math.max(Math.round(header.getBoundingClientRect().bottom), 0);
    details.style.setProperty('--pw-mm-header-offset', `${bottom}px`);
  }

  disconnectedCallback() {
    this.menuObserver?.disconnect();
  }

  select(index) {
    this.activeIndex = index;
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
      const panel = this.panels[i];
      if (panel) panel.hidden = !selected;
    });
  }

  replayActivePanel() {
    const panel = this.panels[this.activeIndex];
    if (!panel) return;
    // Force-reflow trick: clear the inline animation, read a layout property
    // to flush, then restore so the browser starts the keyframe from scratch.
    // Also clears any inline styles left by playCloseCascade() so the entrance
    // animation starts from a clean slate (opacity 1, no transform) on every re-open.
    const items = panel.querySelectorAll('.pw-mm__highlight, .pw-mm__item');
    panel.style.animation = 'none';
    items.forEach(el => {
      el.style.animation = 'none';
      el.style.transition = '';
      el.style.transitionDelay = '';
      el.style.opacity = '';
      el.style.transform = '';
    });
    panel.offsetHeight; // eslint-disable-line no-unused-expressions
    panel.style.animation = '';
    items.forEach(el => { el.style.animation = ''; });
  }

  // Exit cascade (Oner parity): on close, the rows fade + lift slightly, staggered
  // top-to-bottom, while the panel itself cross-fades out (CSS). This runs alongside the
  // panel's graded opacity dissolve so the items leave first and the page cross-fades back —
  // matching Oner's close. Skipped under reduced-motion (the panel just hides). Every inline
  // style set here is cleared by replayActivePanel() on the next open, so it can't leak.
  playCloseCascade() {
    const panel = this.panels[this.activeIndex];
    if (!panel) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const items = panel.querySelectorAll('.pw-mm__highlight, .pw-mm__item');
    if (!items.length) return;
    // Drop the held entrance animation and flush so each row has an explicit opacity:1 start
    // value for the exit transition to interpolate from (without this the browser may batch
    // the change and skip the transition).
    items.forEach(el => { el.style.animation = 'none'; });
    panel.offsetHeight; // eslint-disable-line no-unused-expressions
    items.forEach((el, i) => {
      // Cap the stagger at the 8th row (like the CSS entrance) so a long menu's exit still
      // finishes inside the panel's 0.34s dissolve — otherwise late rows would snap away when
      // header-drawer.js removes [open] and the <details> collapses.
      const delay = Math.min(i, 7) * 0.022;
      el.style.transition = 'opacity 0.18s ease, transform 0.18s ease';
      el.style.transitionDelay = `${delay.toFixed(3)}s`;
      el.style.opacity = '0';
      el.style.transform = 'translateY(-6px)';
    });
  }

  onKeydown(event, i) {
    const last = this.tabs.length - 1;
    let next = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = i === last ? 0 : i + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = i === 0 ? last : i - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.select(next);
    this.tabs[next].focus();
  }
}

if (!customElements.get('powerlete-gender-tabs')) {
  customElements.define('powerlete-gender-tabs', PowerleteGenderTabs);
}
